/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, Form, Alert, Badge, Modal, ListGroup } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useAudioToast from '../hooks/useAudioToast';
import locationService from '../services/locationService';

export default function JobMonitor() {
  // ── Audio toast hook ─────────────────────────────────
  const audioToast = useAudioToast();
  
  // ── Navigation state ─────────────────────────────────
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const navigate = useNavigate();
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);

  const navCollapse = () => {
    document.querySelectorAll('.navbar-collapse.show').forEach(el => el.classList.remove('show'));
    setAdminDropdownOpen(false);
  };

  const toggleAdminDropdown = (e) => {
    e.preventDefault();
    setAdminDropdownOpen(!adminDropdownOpen);
  };

  const logout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const [monitoringStatus, setMonitoringStatus] = useState(null);
  const [isActive, setIsActive] = useState(false);
  const [postcodeAreas, setPostcodeAreas] = useState([]);
  const [alertSettings, setAlertSettings] = useState({
    screenFlash: true,
    soundAlert: true,
    emailAlert: false,
    checkInterval: 5,
    autoStopAfterMinutes: 30
  });
  const [currentJobCount, setCurrentJobCount] = useState(0);
  const [lastCheckTime, setLastCheckTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [newPostcode, setNewPostcode] = useState('');
  const [isAlerting, setIsAlerting] = useState(false);
  const [alertInterval, setAlertInterval] = useState(null);
  const [checkInterval, setCheckInterval] = useState(null);
  const [audioContext, setAudioContext] = useState(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  
  // Location tracking state
  const [locationTracking, setLocationTracking] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  // Refs for audio and visual alerts
  const audioRef = useRef(null);
  const flashRef = useRef(null);
  const soundLoopRef = useRef(null);

  // Initialize audio context on user interaction
  const initializeAudio = () => {
    try {
      if (!audioContext) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const ctx = new AudioCtx();
        setAudioContext(ctx);
        setAudioEnabled(true);
        console.log('Audio context initialized');
      }
    } catch (error) {
      console.log('Failed to initialize audio context:', error);
    }
  };

  // Create a mobile-friendly beep sound
  const createMobileBeep = () => {
    if (!audioContext) return;
    
    try {
      // Resume audio context if suspended (mobile requirement)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } catch (error) {
      console.log('Mobile beep failed:', error);
    }
  };

  // Load monitoring status on component mount
  useEffect(() => {
    loadMonitoringStatus();
  }, []);

  // Set up periodic checking when monitoring is active
  useEffect(() => {
    if (isActive && alertSettings.checkInterval) {
      const interval = setInterval(() => {
        checkAvailableJobs();
      }, alertSettings.checkInterval * 60 * 1000);
      
      setCheckInterval(interval);
      
      // Initial check
      checkAvailableJobs();
      
      return () => {
        if (interval) clearInterval(interval);
      };
    } else {
      if (checkInterval) {
        clearInterval(checkInterval);
        setCheckInterval(null);
      }
    }
  }, [isActive, alertSettings.checkInterval]);

  // Start/stop location tracking when monitoring is active
  useEffect(() => {
    if (isActive) {
      startLocationTracking();
    } else {
      stopLocationTracking();
    }

    return () => {
      stopLocationTracking();
    };
  }, [isActive]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (checkInterval) clearInterval(checkInterval);
      if (alertInterval) clearInterval(alertInterval);
    };
  }, []);

  const loadMonitoringStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/job-monitor/status');
      const status = response.data;
      
      setMonitoringStatus(status);
      setIsActive(status.isActive);
      setPostcodeAreas(status.postcodeAreas || []);
      setAlertSettings(status.alertSettings || {
        screenFlash: true,
        soundAlert: true,
        emailAlert: false,
        checkInterval: 5,
        autoStopAfterMinutes: 30
      });
      setCurrentJobCount(status.currentJobCount || 0);
      setLastCheckTime(status.lastCheckTime);

      // For listing_only users, if no postcode areas are set, populate with their coverage areas
      if (role === 'listing_only' && (!status.postcodeAreas || status.postcodeAreas.length === 0)) {
        try {
          const userResponse = await axios.get('/user/profile');
          const userProfile = userResponse.data;
          
          if (userProfile.postcodeSectorsCovered && userProfile.postcodeSectorsCovered.length > 0) {
            setPostcodeAreas(userProfile.postcodeSectorsCovered);
            audioToast.info('Postcode areas populated from your profile coverage areas');
          } else if (userProfile.mainPostcode) {
            setPostcodeAreas([userProfile.mainPostcode]);
            audioToast.info('Postcode area populated from your main postcode');
          }
        } catch (profileError) {
          console.error('Error loading user profile for postcode areas:', profileError);
        }
      }
    } catch (error) {
      console.error('Error loading monitoring status:', error);
      audioToast.error('Failed to load monitoring status');
    } finally {
      setLoading(false);
    }
  };

  const checkAvailableJobs = async () => {
    try {
      const response = await axios.post('/job-monitor/check');
      const result = response.data;
      
      setCurrentJobCount(result.count);
      setLastCheckTime(new Date());
      
      if (result.shouldAlert && result.hasNewJobs) {
        triggerAlert(result.newJobs);
      }
    } catch (error) {
      console.error('Error checking available jobs:', error);
    }
  };

  // Location tracking functions
  const startLocationTracking = async () => {
    try {
      if (locationTracking) {
        console.log('Location tracking already active');
        return;
      }

      console.log('📍 Starting location tracking...');
      setLocationError(null);

      await locationService.startTracking({
        interval: 30000, // 30 seconds
        highAccuracy: true,
        timeout: 10000
      });

      setLocationTracking(true);
      audioToast.success('Location tracking started');
    } catch (error) {
      console.error('Error starting location tracking:', error);
      setLocationError(error.message);
      audioToast.error(`Location tracking failed: ${error.message}`);
    }
  };

  const stopLocationTracking = () => {
    if (!locationTracking) {
      console.log('Location tracking is not active');
      return;
    }

    console.log('📍 Stopping location tracking...');
    locationService.stopTracking();
    setLocationTracking(false);
    setCurrentLocation(null);
    setLocationError(null);
    toast.info('Location tracking stopped');
  };

  const getCurrentLocation = async () => {
    try {
      const location = await locationService.getCurrentLocation({
        highAccuracy: true,
        timeout: 10000
      });
      
      setCurrentLocation(location);
      setLocationError(null);
      
      // Send location to server
      await locationService.sendLocationToServer(location);
      
      toast.success('Location updated successfully');
      return location;
    } catch (error) {
      console.error('Error getting current location:', error);
      setLocationError(error.message);
      toast.error(`Location error: ${error.message}`);
      throw error;
    }
  };

  const triggerAlert = (newJobs) => {
    if (!isAlerting) {
      setIsAlerting(true);
      
      // Screen flash
      if (alertSettings.screenFlash) {
        startScreenFlash();
      }
      
      // Sound alert (loop until acknowledged)
      if (alertSettings.soundAlert) {
        startAlertSoundLoop();
      }
      
      // Show notification
      toast.info(`🚨 ${newJobs.length} new job(s) available in your area!`, {
        autoClose: false,
        closeOnClick: false,
        draggable: false
      });
      
      // Auto-stop after specified time
      if (alertSettings.autoStopAfterMinutes) {
        const stopTimeout = setTimeout(() => {
          stopAlert();
        }, alertSettings.autoStopAfterMinutes * 60 * 1000);
        
        setAlertInterval(stopTimeout);
      }
    }
  };

  const startScreenFlash = () => {
    if (flashRef.current) {
      flashRef.current.style.animation = 'flash 0.5s infinite';
    }
  };

  const stopScreenFlash = () => {
    if (flashRef.current) {
      flashRef.current.style.animation = 'none';
    }
  };

  const startAlertSoundLoop = () => {
    try {
      if (soundLoopRef.current) return; // already looping

      // Try mobile-friendly beep first
      if (audioContext && audioEnabled) {
        createMobileBeep();
        soundLoopRef.current = setInterval(() => {
          if (isAlerting) {
            createMobileBeep();
          }
        }, 1200);
        return;
      }

      // Fallback to existing methods
      if (typeof window.createAlertSound === 'function') {
        window.createAlertSound();
        soundLoopRef.current = setInterval(() => {
          if (isAlerting) {
            try { window.createAlertSound(); } catch {}
          }
        }, 1200);
        return;
      }

      // Final fallback: loop audio element
      if (audioRef.current) {
        const audio = audioRef.current;
        audio.loop = true;
        audio.currentTime = 0;
        audio.play().catch(err => {
          console.log('Audio autoplay blocked:', err);
          // Last resort: periodic beeps via Web Audio API
          try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            const ctx = new AudioCtx();
            const beep = () => {
              const osc = ctx.createOscillator();
              const gain = ctx.createGain();
              osc.connect(gain);
              gain.connect(ctx.destination);
              osc.type = 'sine';
              osc.frequency.setValueAtTime(800, ctx.currentTime);
              gain.gain.setValueAtTime(0, ctx.currentTime);
              gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.01);
              gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.5);
              osc.start();
              osc.stop(ctx.currentTime + 0.5);
            };
            beep();
            soundLoopRef.current = setInterval(() => { if (isAlerting) beep(); }, 1200);
          } catch (e) {
            console.log('All sound attempts failed:', e);
          }
        });
      }
    } catch (error) {
      console.log('startAlertSoundLoop error:', error);
    }
  };

  const stopAlertSoundLoop = () => {
    try {
      if (soundLoopRef.current) {
        clearInterval(soundLoopRef.current);
        soundLoopRef.current = null;
      }
      if (audioRef.current) {
        const audio = audioRef.current;
        try { audio.pause(); } catch {}
        audio.loop = false;
        try { audio.currentTime = 0; } catch {}
      }
    } catch {}
  };

  const stopAlert = () => {
    setIsAlerting(false);
    stopScreenFlash();
    stopAlertSoundLoop();
    if (alertInterval) {
      clearTimeout(alertInterval);
      setAlertInterval(null);
    }
  };

  const handleToggleMonitoring = async () => {
    try {
      setSaving(true);
      const response = await axios.post('/job-monitor/toggle', {
        isActive: !isActive
      });
      
      const status = response.data;
      setIsActive(status.isActive);
      setMonitoringStatus(status);
      
      if (status.isActive) {
        toast.success('Job monitoring started');
      } else {
        toast.info('Job monitoring stopped');
        stopAlert();
      }
    } catch (error) {
      console.error('Error toggling monitoring:', error);
      toast.error('Failed to toggle monitoring');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      
      // For listing_only users, only save alert settings, not postcode areas
      const settingsToSave = role === 'listing_only' 
        ? { alertSettings }
        : { postcodeAreas, alertSettings };
      
      const response = await axios.post('/job-monitor/settings', settingsToSave);
      
      const status = response.data;
      setMonitoringStatus(status);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPostcode = () => {
    if (newPostcode.trim() && !postcodeAreas.includes(newPostcode.trim().toUpperCase())) {
      setPostcodeAreas([...postcodeAreas, newPostcode.trim().toUpperCase()]);
      setNewPostcode('');
    }
  };

  const handleRemovePostcode = (index) => {
    setPostcodeAreas(postcodeAreas.filter((_, i) => i !== index));
  };

  const loadHistory = async () => {
    try {
      const response = await axios.get('/job-monitor/history');
      setHistory(response.data.history || []);
      setShowHistory(true);
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Failed to load history');
    }
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div ref={flashRef}>
      {/* Sticky Header - Page Content Only */}
      <div className="sticky-top bg-white border-bottom pb-3" style={{ 
        zIndex: 1020,
        top: '56px' // Position below the main navbar
      }}>
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <h4 className="mb-0">Job Monitor</h4>
            </div>
          </div>
        </div>
      </div>

      <div className="container mt-4">
        {/* Audio initialization button for mobile */}
        {!audioEnabled && (
          <div className="alert alert-info mb-3">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <strong>Enable Sound Alerts</strong>
                <br />
                <small>Click the button to enable sound notifications on mobile devices</small>
              </div>
              <Button 
                variant="primary" 
                size="sm"
                onClick={initializeAudio}
              >
                Enable Sound
              </Button>
            </div>
          </div>
        )}

        {/* Test sound button */}
        {audioEnabled && (
          <div className="alert alert-success mb-3">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <strong>Sound Alerts Enabled</strong>
                <br />
                <small>Click "Test Sound" to verify audio is working</small>
              </div>
              <Button 
                variant="outline-success" 
                size="sm"
                onClick={createMobileBeep}
              >
                Test Sound
              </Button>
            </div>
          </div>
        )}

        {/* Hidden audio element for alerts */}
        <audio ref={audioRef} preload="auto" loop>
          <source src="/alert-sound.mp3" type="audio/mpeg" />
          <source src="/alert-sound.wav" type="audio/wav" />
        </audio>

      {/* Alert overlay */}
      {isAlerting && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ 
            backgroundColor: 'rgba(255, 0, 0, 0.1)', 
            zIndex: 9999,
            animation: 'flash 0.5s infinite'
          }}
        >
          <div className="bg-warning p-4 rounded text-center">
            <h2>🚨 NEW JOBS AVAILABLE! 🚨</h2>
            <p>Check your dashboard for new job opportunities!</p>
            <Button variant="primary" onClick={stopAlert}>
              Acknowledge
            </Button>
          </div>
        </div>
      )}

      <div className="row">
        <div className="col-md-8">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Job Monitor</h4>
              <div className="d-flex align-items-center gap-2">
                <Badge bg={isActive ? 'success' : 'secondary'}>
                  {isActive ? 'Active' : 'Inactive'}
                </Badge>
                <Button 
                  variant={isActive ? 'danger' : 'success'}
                  onClick={handleToggleMonitoring}
                  disabled={saving}
                >
                  {isActive ? 'Stop Monitoring' : 'Start Monitoring'}
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <div className="row mb-3">
                <div className="col-md-6">
                  <h5>Current Status</h5>
                  <p><strong>Available Jobs:</strong> {currentJobCount}</p>
                  <p><strong>Last Check:</strong> {lastCheckTime ? new Date(lastCheckTime).toLocaleString() : 'Never'}</p>
                  <p><strong>Check Interval:</strong> {alertSettings.checkInterval} minutes</p>
                </div>
                <div className="col-md-6">
                  <h5>Monitored Areas</h5>
                  {role === 'listing_only' && (
                    <div className="alert alert-info mb-2">
                      <small>
                        <i className="bi bi-info-circle me-1"></i>
                        Your monitored areas are based on your profile coverage areas. 
                        You can view jobs in these areas but cannot accept them.
                      </small>
                    </div>
                  )}
                  <div className="d-flex flex-wrap gap-1">
                    {postcodeAreas.map((area, index) => (
                      <Badge key={index} bg="info" className="me-1">
                        {area}
                        {role !== 'listing_only' && (
                          <Button
                            variant="link"
                            size="sm"
                            className="text-white p-0 ms-1"
                            onClick={() => handleRemovePostcode(index)}
                          >
                            ×
                          </Button>
                        )}
                      </Badge>
                    ))}
                  </div>
                  {role !== 'listing_only' && (
                    <div className="mt-2 d-flex gap-1">
                      <Form.Control
                        type="text"
                        placeholder="Add postcode area (e.g., EX12)"
                        value={newPostcode}
                        onChange={(e) => setNewPostcode(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddPostcode()}
                      />
                      <Button variant="outline-primary" onClick={handleAddPostcode}>
                        Add
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <hr />

              <h5>Alert Settings</h5>
              <div className="row">
                <div className="col-md-6">
                  <Form.Check
                    type="checkbox"
                    label="Screen Flash"
                    checked={alertSettings.screenFlash}
                    onChange={(e) => setAlertSettings({
                      ...alertSettings,
                      screenFlash: e.target.checked
                    })}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Sound Alert"
                    checked={alertSettings.soundAlert}
                    onChange={(e) => setAlertSettings({
                      ...alertSettings,
                      soundAlert: e.target.checked
                    })}
                  />
                  <Form.Check
                    type="checkbox"
                    label="Email Alert"
                    checked={alertSettings.emailAlert}
                    onChange={(e) => setAlertSettings({
                      ...alertSettings,
                      emailAlert: e.target.checked
                    })}
                  />
                  
                  {/* Audio Status Indicator */}
                  {alertSettings.soundAlert && (
                    <div className="mt-2">
                      <small className={`d-block ${audioEnabled ? 'text-success' : 'text-warning'}`}>
                        <i className={`bi ${audioEnabled ? 'bi-volume-up-fill' : 'bi-volume-mute-fill'}`}></i>
                        {' '}Audio: {audioEnabled ? 'Enabled' : 'Disabled - Click "Enable Sound" above'}
                      </small>
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label>Check Interval (minutes)</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="60"
                      value={alertSettings.checkInterval}
                      onChange={(e) => setAlertSettings({
                        ...alertSettings,
                        checkInterval: parseInt(e.target.value)
                      })}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Auto-stop after (minutes)</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="120"
                      value={alertSettings.autoStopAfterMinutes}
                      onChange={(e) => setAlertSettings({
                        ...alertSettings,
                        autoStopAfterMinutes: parseInt(e.target.value)
                      })}
                    />
                  </Form.Group>
                </div>
              </div>

              <div className="mt-3">
                <Button 
                  variant="primary" 
                  onClick={handleSaveSettings}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
                <Button 
                  variant="outline-secondary" 
                  className="ms-2"
                  onClick={loadHistory}
                >
                  View History
                </Button>
                <Button 
                  variant="outline-info" 
                  className="ms-2"
                  onClick={checkAvailableJobs}
                >
                  Check Now
                </Button>
              </div>
            </Card.Body>
          </Card>
        </div>

        <div className="col-md-4">
          <Card>
            <Card.Header>
              <h5>Quick Actions</h5>
            </Card.Header>
            <Card.Body>
              <Button 
                variant="outline-primary" 
                className="w-100 mb-2"
                onClick={checkAvailableJobs}
              >
                Check Available Jobs
              </Button>
              <Button 
                variant="outline-success" 
                className="w-100 mb-2"
                as={Link}
                to="/dashboard"
              >
                View Available Jobs
              </Button>
              {role !== 'listing_only' && (
                <Button 
                  variant="outline-info" 
                  className="w-100 mb-2"
                  as={Link}
                  to="/dashboard/accepted-jobs"
                >
                  My Accepted Jobs
                </Button>
              )}
            </Card.Body>
          </Card>
          
          {/* Location Tracking Card */}
          <Card className="mt-3">
            <Card.Header>
              <h5>📍 Location Tracking</h5>
            </Card.Header>
            <Card.Body>
              {locationError && (
                <Alert variant="danger" className="mb-3">
                  <strong>Location Error:</strong> {locationError}
                </Alert>
              )}
              
              {currentLocation && (
                <div className="mb-3">
                  <small className="text-muted">Current Location:</small>
                  <div className="small">
                    <strong>Lat:</strong> {currentLocation.latitude.toFixed(6)}<br/>
                    <strong>Lng:</strong> {currentLocation.longitude.toFixed(6)}<br/>
                    {currentLocation.address && (
                      <>
                        <strong>Address:</strong> {currentLocation.address}<br/>
                      </>
                    )}
                    <strong>Accuracy:</strong> {currentLocation.accuracy ? `${Math.round(currentLocation.accuracy)}m` : 'Unknown'}<br/>
                    <strong>Updated:</strong> {new Date(currentLocation.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              )}
              
              <div className="d-grid gap-2">
                <Button 
                  variant={locationTracking ? "danger" : "success"}
                  onClick={locationTracking ? stopLocationTracking : startLocationTracking}
                  disabled={!isActive}
                >
                  {locationTracking ? 'Stop Location Tracking' : 'Start Location Tracking'}
                </Button>
                
                <Button 
                  variant="outline-primary"
                  onClick={getCurrentLocation}
                  disabled={!isActive}
                >
                  Update Location Now
                </Button>
              </div>
              
              {!isActive && (
                <small className="text-muted d-block mt-2">
                  Location tracking is only available when job monitoring is active.
                </small>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* History Modal */}
      <Modal show={showHistory} onHide={() => setShowHistory(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Alert History</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {history.length === 0 ? (
            <p className="text-muted">No alert history available.</p>
          ) : (
            <ListGroup>
              {history.map((record, index) => (
                <ListGroup.Item key={index}>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <strong>{new Date(record.timestamp).toLocaleString()}</strong>
                      <br />
                      <small className="text-muted">
                        Jobs: {record.previousCount} → {record.newCount} 
                        ({record.newJobs.length} new)
                      </small>
                    </div>
                    <Badge bg="success">{record.newJobs.length} new</Badge>
                  </div>
                  {record.newJobs.length > 0 && (
                    <div className="mt-2">
                      <small><strong>New Jobs:</strong></small>
                      <ul className="list-unstyled mt-1">
                        {record.newJobs.map((job, jobIndex) => (
                          <li key={jobIndex} className="small">
                            {job.customerName} - {job.from} → {job.to}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Modal.Body>
      </Modal>

      </div>

      <ToastContainer position="top-right" />
      
      <style jsx>{`
        @keyframes flash {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
} 